import {
  Eye,
  Farseer,
  MethodSubtool,
  Phase,
  Squares,
  TheorySubtool,
} from "assets/icons";
import { SelectField } from "components/form/fields";
import { Message } from "components/message/Message";
import { SearchField } from "components/search/components";
import { FC, useCallback, useEffect, useState } from "react";
import { i18nService, SearchService } from "services";
import { EntityType, IFieldType, IOption, ISearchResult } from "types";
import { StringParam, useQueryParam } from "use-query-params";

export interface IToolboxSearchData {
  subToolTypeOptions: IOption[];
  phaseOptions: IOption[];
}

interface Props {
  toolboxSearchData: IToolboxSearchData;
  parentToolId: string;
}

export const ToolboxSearch: FC<Props> = ({
  toolboxSearchData: { subToolTypeOptions, phaseOptions },
  parentToolId,
}) => {
  const [term, setTerm] = useState<{ search: string }>({ search: "" });
  const [subToolType, setSubToolType] = useQueryParam(
    "sub-tool-type",
    StringParam
  );
  const [phase, setPhase] = useQueryParam("fase", StringParam);
  const [results, setResults] = useState<ISearchResult[]>([]);

  const search = useCallback(async () => {
    try {
      const results = await SearchService.search({
        page: 1,
        amount: -1,
        query: term.search,
        type: EntityType.subTool,
        facets: {
          ...(subToolType && { subToolType: [subToolType] }),
          ...(phase && { phase: [phase] }),
          parentTool: [parentToolId],
        },
        prioritiseDossiersAndTools: false,
      });

      setResults(results.results);
    } catch (error) {
      console.error(error);
    }
  }, [parentToolId, phase, subToolType, term.search]);

  useEffect(() => {
    search();
  }, [term, subToolType, phase, search]);

  const renderSVG = (subToolType: string) => {
    switch (subToolType) {
      case "theory":
        return <TheorySubtool />;
      case "method":
        return <MethodSubtool />;
      case "example":
        return <Eye />;
      case "template":
        return <Farseer />;
    }
  };

  return (
    <>
      <SearchField id={"test"} onChange={setTerm} searchType="onChange" />
      <div className="table-overview-filters">
        <SelectField
          options={subToolTypeOptions}
          placeholder="Type content"
          prefixText="Ik zoek een"
          hideLabel
          type={IFieldType.select}
          labelIcon={<Squares />}
          name="subToolType"
          label="Type"
          value={subToolType || ""}
          onChange={({ currentTarget: { value } }) => setSubToolType(value)}
        />
        <SelectField
          options={phaseOptions}
          placeholder="Phase"
          prefixText="binnen"
          hideLabel
          type={IFieldType.select}
          labelIcon={<Phase />}
          name="Phase"
          label="Fase"
          value={phase || ""}
          onChange={({ currentTarget: { value } }) => setPhase(value)}
        />
      </div>
      <div className="section">
        <div className="section__content">
          {!!results.length ? (
            <ul className="grid of-3">
              {results?.map((result) => (
                <li>
                  <a className="teaser" href={result.document.url}>
                    <div className="teaser__media">
                      <img
                        className="teaser__image lozad"
                        data-src={result.document.metaImage}
                        alt={result.document.title}
                        src={result.document.metaImage}
                        data-loaded="true"
                      />
                      <span className="teaser__media-label">
                        <span className="label is-light">
                          <span className="label__icon">
                            {renderSVG(result.document.subToolType?.key!)}
                          </span>
                          <span className="label__text">
                            {result.document.subToolType?.label}
                          </span>
                        </span>
                      </span>
                    </div>
                    <div className="teaser__info">
                      <h2 className="teaser__title">
                        <span>{result.document.title}</span>
                      </h2>

                      <p className="teaser__description">
                        {result.document.intro
                          .split(". ")
                          .slice(0, 3)
                          .join(". ")}
                        {result.document.intro.split(". ").length > 3 && " ..."}
                      </p>
                    </div>
                  </a>
                </li>
              ))}
            </ul>
          ) : (
            <Message>{i18nService.copy["siteSearch.noResults"]}</Message>
          )}
        </div>
      </div>
    </>
  );
};
